import { useEffect, useState } from "react";
import { useAuth, useCompany, useDataslotAuth } from "@x-veloper/dataslot-auth";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription } from "./ui/alert";

interface DataslotAuthWrapperProps {
  children: React.ReactNode;
}

export default function DataslotAuthWrapper({ children }: DataslotAuthWrapperProps) {
  useAuth();
  const { selectedCompany, loading: companyLoading, hasCompanyAccess, updateSelectedCompanyGuid } = useCompany();
  const { signInWithBackendGuid, canSignIn, loading: dataslotLoading, currentGuid } = useDataslotAuth();
  const [error, setError] = useState<string | null>(null);
  const [hasAttempted, setHasAttempted] = useState(false);

  // Effect 1: Sign in to Dataslot when company is selected
  useEffect(() => {
    if (canSignIn && selectedCompany && !currentGuid && !hasAttempted && !dataslotLoading) {
      setHasAttempted(true);
      signInWithBackendGuid().catch((err) => {
        console.error("[DataslotAuthWrapper] Failed to sign in to Dataslot:", err);
        setError(err.message || "Failed to sign in to Dataslot");
      });
    }
  }, [canSignIn, selectedCompany, currentGuid, hasAttempted, dataslotLoading, signInWithBackendGuid]);

  // Effect 2: Sync Dataslot gUId to CompanyContext after successful sign-in
  useEffect(() => {
    if (currentGuid && selectedCompany?.gUId && selectedCompany.gUId !== currentGuid) {
      console.log('[DataslotAuthWrapper] Syncing Dataslot gUId to CompanyContext:', {
        company: selectedCompany.company,
        oldGuid: selectedCompany.gUId.substring(0, 8) + '...',
        newGuid: currentGuid.substring(0, 8) + '...',
      });

      try {
        updateSelectedCompanyGuid(currentGuid);
      } catch (err) {
        console.error('[DataslotAuthWrapper] Failed to sync gUId:', err);
        // Don't set error state - this is not critical, API will still work
      }
    }
  }, [currentGuid, selectedCompany?.gUId, updateSelectedCompanyGuid]);

  // Show loading while companies are being loaded
  if (companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-center mt-4 text-gray-600">Loading company information...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error if no company access
  if (!hasCompanyAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>No Company Access</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>
                You don't have access to MasterLabel. Please contact your administrator.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading while signing in to Dataslot
  if (dataslotLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Connecting to Backend...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-center mt-4 text-gray-600">
              Signing in to Dataslot for {selectedCompany?.company}...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error if Dataslot sign-in failed
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Error</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <button
              className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => {
                setError(null);
                setHasAttempted(false);
              }}
            >
              Retry
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render children when everything is ready
  return <>{children}</>;
}